import { cn } from '@shared/utils/utils';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '../index';

export interface ProductImageOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  src: string;
  name: string;
  code?: string;
  acronym?: string;
  'data-testid'?: string;
}

export const ProductImageOverlay = ({
  isOpen,
  onClose,
  src,
  name,
  code,
  'data-testid': testId = 'product-image-overlay',
}: Omit<ProductImageOverlayProps, 'acronym'>) => {
  // Ajustar el tipo para quitar acronym
  // Cerrar con tecla Escape y bloquear scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const overlayContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testId}
    >
      {/* Backdrop con blur */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-lg" />

      {/* Contenedor de imagen flotante */}
      <div
        className="relative animate-in zoom-in-95 duration-300 ease-out max-w-[95vw] max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Footer con información - ahora arriba */}
        <div className="bg-body-bg border-card-border border p-6 rounded-t-2xl shadow-2xl">
          <div className="flex justify-between">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate mb-1">{name}</p>
                {code && (
                  <Badge
                    variant="transparent-blue"
                    className="mt-1 text-sm rounded-full"
                    data-testid={`${testId}-code`}
                  >
                    {code}
                  </Badge>
                )}
              </div>
            </div>

            {/* Botón de cerrar */}
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-all duration-200 hover:scale-105 ml-3 flex-shrink-0"
              data-testid={`${testId}-close`}
              aria-label="Cerrar imagen"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Imagen principal */}
        <div className="relative group flex-1 min-h-0">
          {/* Sombra y efectos de fondo */}
          <div className="absolute -inset-6  rounded-3xl blur-3xl opacity-60 group-hover:opacity-80 transition-opacity duration-500" />

          {/* Contenedor de imagen con mejor ajuste */}
          <div className="relative bg-background rounded-b-2xl border border-card-border border-t-0 overflow-hidden h-full flex items-center justify-center">
            <img
              src={src}
              alt={name}
              className={cn(
                'block object-contain transition-all duration-300 group-hover:scale-[1.01]',
                // Ajustes responsivos mejorados - dejamos espacio para el footer
                'w-auto h-auto max-w-full',
                'max-w-[90vw] max-h-[calc(70vh-80px)]',
                'sm:max-w-[80vw] sm:max-h-[calc(75vh-80px)]',
                'md:max-w-[70vw] md:max-h-[calc(80vh-80px)]',
                'lg:max-w-[60vw] lg:max-h-[calc(85vh-80px)]'
              )}
              data-testid={`${testId}-image`}
            />

            {/* Overlay gradiente sutil para mejor contraste */}
            <div className="absolute inset-0 bg-gradient-to-t from-background/8 via-transparent to-transparent pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );

  // Usar portal para renderizar en el body y evitar problemas de z-index
  return createPortal(overlayContent, document.body);
};
